import {
  rotuloCriticidade,
  rotuloCriticidadeApia,
  rotuloStatus,
  type Criticidade,
  type CriticidadeApia,
  type StatusInspecao,
} from "@qsms/shared";
import { useLiveQuery } from "dexie-react-hooks";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CampoBusca } from "../componentes/CampoBusca.js";
import { CartaoConexao } from "../componentes/CartaoConexao.js";
import { InstalarApp } from "../componentes/InstalarApp.js";
import {
  CHAVES,
  banco,
  gravarAjuste,
  lerAjuste,
  type InspecaoLocal,
} from "../dados/banco.js";
import { criarInspecao } from "../dados/criarInspecao.js";

/// Tela inicial: obra, checklist, e o histórico de inspeções — de todas as obras
/// quando nenhuma está escolhida, ou só da escolhida.
///
/// Tudo vem do IndexedDB — a tela funciona igual com ou sem rede, e é por isso que
/// não há indicador de "carregando" nem estado vazio por falha de rede.
///
/// Obra e checklist ficam fixados no aparelho e visíveis o tempo todo. Um inspetor
/// passa a manhã aplicando o mesmo checklist na mesma obra; obrigá-lo a reescolher
/// a cada registro seria cobrar dois toques por inspeção, com luva, no sol.

export function Inicio() {
  const [busca, setBusca] = useState("");
  const [filtroStatus, setFiltroStatus] = useState<StatusInspecao | "">("");
  const [criando, setCriando] = useState(false);
  const [escolhendoObra, setEscolhendoObra] = useState(false);
  const [buscaObra, setBuscaObra] = useState("");
  const [limite, setLimite] = useState({ assinatura: "", n: POR_VEZ });
  const navegar = useNavigate();

  const obras = useLiveQuery(
    () => banco.obras.orderBy("codigo").toArray(),
    [],
    [],
  );
  const obraSelecionada = useLiveQuery(
    () => lerAjuste<string | null>(CHAVES.obraSelecionada, null),
    [],
    null,
  );
  const checklistSelecionado = useLiveQuery(
    () => lerAjuste<string | null>(CHAVES.checklistSelecionado, null),
    [],
    null,
  );

  // Com uma obra só, não faz sentido pedir para escolher.
  const obraAtiva =
    obraSelecionada ?? (obras.length === 1 ? obras[0]!.id : null);

  // O checklist fixado pode ter saído do ar entre uma sincronização e outra; nesse
  // caso ele não conta como escolhido, senão o botão criaria uma inspeção órfã.
  const checklist = useLiveQuery(
    () =>
      checklistSelecionado
        ? banco.checklists.get(checklistSelecionado)
        : undefined,
    [checklistSelecionado],
  );
  const checklistAtivo = checklist?.id ?? null;

  // Sem obra escolhida, o histórico inteiro que está no aparelho: é o que o
  // Power Apps mostrava ao abrir. Com obra, só o dela.
  const inspecoes = useLiveQuery(
    async () => {
      if (!obraAtiva) {
        return banco.inspecoes.orderBy("registradoEm").reverse().toArray();
      }
      const lista = await banco.inspecoes
        .where("obraId")
        .equals(obraAtiva)
        .toArray();
      return lista.sort((a, b) => b.registradoEm.localeCompare(a.registradoEm));
    },
    [obraAtiva],
    [],
  );

  const obraPorId = new Map(obras.map((o) => [o.id, o]));

  const visiveis = inspecoes.filter((i) => {
    if (filtroStatus && i.status !== filtroStatus) return false;
    if (!busca) return true;
    const alvo = busca.toLowerCase();
    const daObra = obraAtiva ? undefined : obraPorId.get(i.obraId);
    return (
      i.codigo.toLowerCase().includes(alvo) ||
      (i.titulo ?? "").toLowerCase().includes(alvo) ||
      // Na lista de todas as obras, buscar "0101" ou o nome da obra também acha.
      Boolean(daObra?.codigo.toLowerCase().includes(alvo)) ||
      Boolean(daObra?.nome.toLowerCase().includes(alvo))
    );
  });

  // São milhares de inspeções no histórico; desenhar todas de uma vez travaria o
  // celular. Vai de POR_VEZ em POR_VEZ, e recomeça do topo quando o filtro muda.
  const assinatura = `${obraAtiva}|${busca}|${filtroStatus}`;
  const quantas = limite.assinatura === assinatura ? limite.n : POR_VEZ;
  const exibidas = visiveis.slice(0, quantas);

  const pendentes = inspecoes.filter(
    (i) => i.sincronizacao !== "ENVIADO",
  ).length;

  async function comecar() {
    if (!obraAtiva || !checklistAtivo || criando) return;
    setCriando(true);
    try {
      const id = await criarInspecao(obraAtiva, checklistAtivo);
      navegar(`/inspecao/${id}`);
    } finally {
      setCriando(false);
    }
  }

  if (obras.length === 0) {
    return (
      <Vazio
        titulo="Nenhuma obra disponível"
        texto="Seu usuário ainda não está vinculado a nenhuma obra, ou os cadastros ainda não foram baixados. Conecte-se à internet e aguarde a sincronização."
      />
    );
  }

  const obra = obras.find((o) => o.id === obraAtiva);
  // A lista de obras abre só com um toque: aberta sozinha, ela empurraria o
  // histórico para baixo da tela.
  const trocandoObra = escolhendoObra;

  // Com 65 obras a lista não cabe na tela de um celular; sem busca, escolher a obra
  // vira rolagem. Casa por código e por nome.
  const obrasVisiveis = buscaObra
    ? obras.filter((o) => {
        const termo = buscaObra.toLowerCase();
        return (
          o.codigo.toLowerCase().includes(termo) ||
          o.nome.toLowerCase().includes(termo)
        );
      })
    : obras;

  // Obra e checklist contam como filtro só quando foram escolhidos: com uma obra
  // só, ela vem marcada sozinha e não há o que limpar.
  const temFiltro = Boolean(
    busca || filtroStatus || obraSelecionada || checklistSelecionado,
  );

  /// Volta a tela ao estado de quem acabou de abrir o app. Obra e checklist
  /// também saem: estão guardados no aparelho, e sem este botão a única forma de
  /// desfazer a escolha era trocar por outra.
  function limparFiltros() {
    setBusca("");
    setFiltroStatus("");
    setBuscaObra("");
    setEscolhendoObra(false);
    void gravarAjuste(CHAVES.obraSelecionada, null);
    void gravarAjuste(CHAVES.checklistSelecionado, null);
  }

  // No computador, duas colunas: à esquerda o que se escolhe para começar, à direita
  // o histórico. No celular, uma embaixo da outra, como sempre foi.
  return (
    <section className="lg:grid lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)] lg:items-start lg:gap-6">
      <div>
        <InstalarApp />
        <CartaoConexao />

        {temFiltro && (
          <div className="-mt-1 mb-2 flex justify-end">
            <button
              onClick={limparFiltros}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-marinho hover:bg-marinho-50"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                aria-hidden
              >
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              </svg>
              Limpar filtros
            </button>
          </div>
        )}

        {/* Obra e checklist: os dois que precisam estar preenchidos para começar. */}
        <div className="mb-4 space-y-2">
          <Seletor
            rotulo="Obra"
            valor={obra ? `${obra.codigo} — ${obra.nome}` : null}
            vazio="Selecionar obra"
            onClick={() => setEscolhendoObra(!escolhendoObra)}
            aberto={escolhendoObra}
            desativado={obras.length === 1}
          />

          {trocandoObra && (
            <div className="rounded-lg bg-slate-50 p-2">
              {obras.length > 6 && (
                <div className="mb-2">
                  <CampoBusca
                    valor={buscaObra}
                    aoMudar={setBuscaObra}
                    placeholder="Buscar obra por código ou nome"
                  />
                </div>
              )}
              {obrasVisiveis.length === 0 && (
                <p className="px-2 py-6 text-center text-sm text-slate-500">
                  Nenhuma obra encontrada.
                </p>
              )}
              <ul className="max-h-72 space-y-1.5 overflow-y-auto">
                {obrasVisiveis.map((o) => (
                  <li key={o.id}>
                    <button
                      onClick={() => {
                        void gravarAjuste(CHAVES.obraSelecionada, o.id);
                        setEscolhendoObra(false);
                        setBuscaObra("");
                      }}
                      className={`flex w-full items-center gap-2 rounded-lg border p-3 text-left ${
                        o.id === obraAtiva
                          ? "border-marinho bg-marinho-50"
                          : "border-slate-200 bg-white hover:border-marinho"
                      }`}
                    >
                      <span className="etiqueta shrink-0 bg-marinho-50 font-mono text-marinho">
                        {o.codigo}
                      </span>
                      <span className="min-w-0 truncate font-medium text-slate-800">
                        {o.nome}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Seletor
            rotulo="Checklist"
            valor={
              checklist
                ? checklist.codigo
                  ? `${checklist.codigo} — ${checklist.nome}`
                  : checklist.nome
                : null
            }
            vazio={
              checklistSelecionado && !checklist
                ? "Checklist indisponível — escolher outro"
                : "Selecionar checklist"
            }
            onClick={() => navegar("/nova")}
            alerta={Boolean(checklistSelecionado) && !checklist}
          />
        </div>

        <button
          onClick={() => void comecar()}
          disabled={!obraAtiva || !checklistAtivo || criando}
          className="botao-destaque mb-5 w-full text-base"
        >
          {criando ? "Criando…" : "+ Nova ação"}
        </button>

        {(!obraAtiva || !checklistAtivo) && (
          <p className="-mt-3 mb-5 text-center text-xs text-slate-500">
            Escolha {!obraAtiva ? "a obra" : ""}
            {!obraAtiva && !checklistAtivo ? " e " : ""}
            {!checklistAtivo ? "o checklist" : ""} para liberar o botão.
          </p>
        )}
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
          {obra ? `Histórico da obra ${obra.codigo}` : "Histórico de todas as obras"}
        </h2>
        <div className="mb-4 space-y-2">
          <CampoBusca
            valor={busca}
            aoMudar={setBusca}
            placeholder={
              obraAtiva
                ? "Buscar inspeção por código ou título"
                : "Buscar por código, título ou obra"
            }
          />
          <div className="flex gap-2 overflow-x-auto pb-1">
            <Pilula
              ativo={filtroStatus === ""}
              onClick={() => setFiltroStatus("")}
            >
              Todas ({inspecoes.length})
            </Pilula>
            {(
              [
                "RASCUNHO",
                "EM_ANDAMENTO",
                "FINALIZADO",
                "ASSINADO",
              ] as StatusInspecao[]
            ).map((s) => {
              const n = inspecoes.filter((i) => i.status === s).length;
              if (n === 0) return null;
              return (
                <Pilula
                  key={s}
                  ativo={filtroStatus === s}
                  onClick={() => setFiltroStatus(s)}
                >
                  {rotuloStatus[s]} ({n})
                </Pilula>
              );
            })}
          </div>
        </div>

        {pendentes > 0 && (
          <p className="mb-3 rounded-lg bg-marinho-50 px-3 py-2 text-sm text-marinho">
            {pendentes}{" "}
            {pendentes === 1
              ? "inspeção ainda não foi enviada"
              : "inspeções ainda não foram enviadas"}
            . Elas sobem sozinhas quando houver internet.
          </p>
        )}

        {visiveis.length === 0 ? (
          <Vazio
            titulo={
              inspecoes.length === 0
                ? obraAtiva
                  ? "Nenhuma inspeção nesta obra"
                  : "Nenhuma inspeção registrada ainda"
                : "Nada encontrado"
            }
            texto={
              inspecoes.length === 0
                ? 'Toque em "Nova ação" para registrar a primeira.'
                : "Ajuste a busca ou o filtro."
            }
          />
        ) : (
          <>
            <ul className="grid gap-2 xl:grid-cols-2 2xl:grid-cols-3">
              {exibidas.map((i) => (
                <li key={i.id}>
                  <CartaoInspecao
                    inspecao={i}
                    obra={obraAtiva ? undefined : obraPorId.get(i.obraId)}
                  />
                </li>
              ))}
            </ul>
            {visiveis.length > exibidas.length && (
              <button
                onClick={() => setLimite({ assinatura, n: quantas + POR_VEZ })}
                className="botao-neutro mt-3 w-full"
              >
                Mostrar mais ({(visiveis.length - exibidas.length).toLocaleString("pt-BR")}{" "}
                restantes)
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/// Quantas inspeções a lista desenha por vez.
const POR_VEZ = 50;

function CartaoInspecao({
  inspecao,
  obra,
}: {
  inspecao: InspecaoLocal;
  /// Só na lista de todas as obras: com uma obra escolhida, repetir o nome dela em
  /// cada cartão seria ruído.
  obra?: { codigo: string; nome: string };
}) {
  const checklist = useLiveQuery(
    () => banco.checklists.get(inspecao.checklistId),
    [inspecao.checklistId],
  );
  const editavel = !inspecao.remota && inspecao.status !== "ASSINADO";
  const destino = editavel
    ? `/inspecao/${inspecao.id}`
    : `/inspecao/${inspecao.id}/ver`;

  return (
    <Link to={destino} className="cartao block p-4 hover:border-marinho">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs text-slate-500">
            <span className="font-mono">{inspecao.codigo}</span>
            {obra && (
              <>
                {" · "}
                <span className="font-semibold text-marinho">{obra.codigo}</span> {obra.nome}
              </>
            )}
          </p>
          <p className="truncate font-semibold text-slate-900">
            {inspecao.titulo || checklist?.nome || "Sem título"}
          </p>
          {checklist && inspecao.titulo && (
            <p className="truncate text-xs text-slate-500">{checklist.nome}</p>
          )}
        </div>
        <SinalSincronizacao inspecao={inspecao} />
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <EtiquetaStatus status={inspecao.status} />
        {inspecao.fotos.length > 0 && (
          <span className="etiqueta bg-slate-100 text-slate-600">
            {inspecao.fotos.length}{" "}
            {inspecao.fotos.length === 1 ? "foto" : "fotos"}
          </span>
        )}
        <span className="ml-auto text-xs text-slate-500">
          {new Date(inspecao.registradoEm).toLocaleDateString("pt-BR")}
        </span>
      </div>

      {inspecao.problema && (
        <p className="mt-2 rounded bg-moderada/10 px-2 py-1.5 text-xs text-moderada">
          {inspecao.problema}
        </p>
      )}
    </Link>
  );
}

function SinalSincronizacao({ inspecao }: { inspecao: InspecaoLocal }) {
  if (inspecao.remota) return null;
  switch (inspecao.sincronizacao) {
    case "ENVIADO":
      return (
        <span
          className="shrink-0 text-leve"
          title="Enviado ao servidor"
          aria-label="Enviado"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path
              d="M5 13l4 4 10-11"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      );
    case "BLOQUEADO":
      return (
        <span
          className="shrink-0 text-alta"
          title="Precisa da sua atenção"
          aria-label="Precisa de atenção"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M12 8v5m0 3.5v.5" strokeLinecap="round" />
            <circle cx="12" cy="12" r="9" />
          </svg>
        </span>
      );
    default:
      return (
        <span
          className="shrink-0 text-slate-400"
          title="Aguardando envio"
          aria-label="Aguardando envio"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" strokeLinecap="round" />
          </svg>
        </span>
      );
  }
}

const CORES_STATUS: Record<StatusInspecao, string> = {
  RASCUNHO: "bg-slate-100 text-slate-600",
  EM_ANDAMENTO: "bg-marinho-50 text-marinho",
  FINALIZADO: "bg-limao-50 text-limao-700",
  ASSINADO: "bg-leve/10 text-leve",
};

export function EtiquetaStatus({ status }: { status: StatusInspecao }) {
  return (
    <span className={`etiqueta ${CORES_STATUS[status]}`}>
      {rotuloStatus[status]}
    </span>
  );
}

const CORES_CRITICIDADE: Record<Criticidade, string> = {
  LEVE: "bg-leve/10 text-leve",
  MODERADA: "bg-moderada/10 text-moderada",
  ALTA: "bg-alta/10 text-alta",
};

export function EtiquetaCriticidade({
  criticidade,
}: {
  criticidade: Criticidade;
}) {
  return (
    <span className={`etiqueta ${CORES_CRITICIDADE[criticidade]}`}>
      {rotuloCriticidade[criticidade]}
    </span>
  );
}

const CORES_CRITICIDADE_APIA: Record<CriticidadeApia, string> = {
  BAIXA: "bg-leve/10 text-leve",
  MEDIA: "bg-moderada/10 text-moderada",
  ALTA: "bg-alta/10 text-alta",
};

/// A revisão dos administradores. Sai com o prefixo "Ápia" e uma borda porque, lado a lado
/// com a criticidade do inspetor, duas etiquetas escritas "Alta" seriam lidas como
/// repetição — e são coisas diferentes, feitas por pessoas diferentes.
export function EtiquetaCriticidadeApia({
  criticidade,
}: {
  criticidade: CriticidadeApia;
}) {
  return (
    <span
      className={`etiqueta border border-current/30 ${CORES_CRITICIDADE_APIA[criticidade]}`}
      title="Criticidade Ápia, definida por um administrador na revisão"
    >
      Ápia: {rotuloCriticidadeApia[criticidade]}
    </span>
  );
}

function Pilula({
  ativo,
  onClick,
  children,
}: {
  ativo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition ${
        ativo
          ? "bg-marinho text-white"
          : "bg-white text-slate-600 ring-1 ring-slate-200"
      }`}
    >
      {children}
    </button>
  );
}

function Vazio({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <div className="cartao px-6 py-10 text-center">
      <p className="font-semibold text-slate-700">{titulo}</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">{texto}</p>
    </div>
  );
}

/// Botão que mostra o que está escolhido, ou convida a escolher. O estado vazio
/// precisa ser visivelmente diferente do preenchido: é ele que explica por que o
/// "Nova ação" está apagado.
function Seletor({
  rotulo,
  valor,
  vazio,
  onClick,
  aberto,
  desativado,
  alerta,
}: {
  rotulo: string;
  valor: string | null;
  vazio: string;
  onClick: () => void;
  aberto?: boolean;
  desativado?: boolean;
  alerta?: boolean;
}) {
  const preenchido = Boolean(valor);
  return (
    <button
      onClick={onClick}
      disabled={desativado}
      aria-expanded={aberto}
      className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition disabled:opacity-100 ${
        alerta
          ? "border-moderada/50 bg-moderada/5"
          : preenchido
            ? "border-slate-200 bg-white"
            : "border-dashed border-marinho/40 bg-marinho-50"
      }`}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
          {rotulo}
        </span>
        <span
          className={`block truncate font-semibold ${
            alerta
              ? "text-moderada"
              : preenchido
                ? "text-slate-900"
                : "text-marinho"
          }`}
        >
          {valor ?? vazio}
        </span>
      </span>
      {!desativado && (
        <span aria-hidden className="shrink-0 text-slate-400">
          {aberto ? "▲" : "▾"}
        </span>
      )}
    </button>
  );
}
